import { useState, useEffect } from 'react';
import { MatchState } from '../types';
import { PersistedMatchData } from './useMatchPersistence';
import { SetupData } from './useMatchData';

type ScreenType = 'recovery_modal' | 'before_match' | 'setup_screen';

interface UseScreenNavigationProps {
  // États depuis App.tsx
  dataSource: string;
  isInitialized: boolean;
  matchData: any;
  setupData: SetupData | null;
  savedMatch: PersistedMatchData | null;
  showRecoveryModal: boolean;
  realStateInitialized: boolean;
  matchState: MatchState;
}

interface UseScreenNavigationReturn {
  screenToShow: ScreenType;
  forceSetupScreen: boolean;
  setForceSetupScreen: (force: boolean) => void;
}

/**
 * Hook pour gérer la navigation entre les écrans de l'application
 * Code extrait EXACTEMENT d'App.tsx sans modification
 */
export function useScreenNavigation({
  dataSource,
  isInitialized,
  matchData,
  setupData,
  savedMatch,
  showRecoveryModal,
  realStateInitialized,
  matchState
}: UseScreenNavigationProps): UseScreenNavigationReturn {

  // État pour forcer l'affichage de MatchSetupScreen (code exact d'App.tsx)
  const [forceSetupScreen, setForceSetupScreen] = useState(false);

  // Reset du flag forceSetupScreen quand on a de nouvelles données (code exact d'App.tsx)
  useEffect(() => {
    if (forceSetupScreen && dataSource && isInitialized) {
      setForceSetupScreen(false);
    }
  }, [forceSetupScreen, dataSource, isInitialized]);

  // Fonction centralisée pour déterminer l'écran à afficher (code exact d'App.tsx)
  const determineScreenToShow = (): ScreenType => {
    // Si forcé vers setup screen
    if (forceSetupScreen) {
      return 'setup_screen';
    }

    const hasMatchInProgress = savedMatch && (savedMatch.progress.isStarted && !savedMatch.progress.isFinished);
    const hasStorageData = savedMatch !== null;
    const hasParameters = dataSource && isInitialized;
    const isRefreshOnActiveScreen = !showRecoveryModal && realStateInitialized && 
      (matchState.currentScreen === 'during' || matchState.currentScreen === 'after');


    // ✅ NOUVELLE APPROCHE : Vérifier dans les données sauvegardées si on était sur 'after'
    const wasOnAfterScreen = savedMatch?.matchState?.currentScreen === 'after' || 
                            (savedMatch?.matchState as any)?.cs === 'after'; // Format compressé

    // Si on a des données sauvegardées et qu'on était sur AfterMatch ou DuringMatch
    if (hasStorageData && (hasMatchInProgress || wasOnAfterScreen)) {
      return 'recovery_modal';
    }

    // Cas 1: Refresh sur DuringMatch ou AfterMatch -> toujours modal recovery si match en cours
    if (isRefreshOnActiveScreen && hasMatchInProgress) {
      return 'recovery_modal';
    }

    // Cas 2: Match en cours -> modal recovery
    if (hasMatchInProgress && !isRefreshOnActiveScreen) {
      return 'recovery_modal';
    }

    // QR setup data → setup screen (after recovery checks)
    if (setupData) {
      return 'setup_screen';
    }

    // Cas 3: Avec paramètres mais pas de match en cours -> BeforeMatch
    if (hasParameters && !hasMatchInProgress) {
      return 'before_match';
    }

    // Cas 4: Sans paramètres mais avec données storage -> BeforeMatch (avec bouton toujours visible)
    if (!hasParameters && hasStorageData && !hasMatchInProgress) {
      return 'before_match';
    }

    // Cas 5: Si on a des données initialisées mais pas de match en cours -> BeforeMatch
    if (isInitialized && matchData && !hasMatchInProgress) {
      return 'before_match';
    }

    // Cas 6: Aucune donnée -> MatchSetupScreen
    return 'setup_screen';
  };

  const screenToShow = determineScreenToShow();

  return {
    screenToShow,
    forceSetupScreen,
    setForceSetupScreen
  };
}